/**
 * Shaka adapter. Vite aliases `'shaka-player'` to `shaka-player/dist/shaka-player.ui.js` so this
 * single import pulls in the `.ui` bundle (player + `shaka.ui.*`) instead of the player-only
 * `shaka-player.compiled.js`. Avoids referencing the `.ui.js` path directly, which TS would resolve
 * to its sibling `.ui.d.ts` (a script-style ambient declaration, not a module).
 */
import shaka from 'shaka-player';
import 'shaka-player/dist/controls.css';
import '../styles/material-icons-round.css';
import {
  DEFAULT_KEYBOARD_LARGE_SEEK_SEC,
  DEFAULT_KEYBOARD_SMALL_SEEK_SEC,
} from './keyboardControls';
import { isFullscreenActive } from './fullscreen';
import { isLiveStreamPlayback } from './media';
import { ensureShakaCombinedLanguageCaptionsRegistered } from './shakaCombinedLanguageCaptions';
import {
  addSidecarCaptionTracks,
  collectSidecarCaptionsFromVideo,
  mergeSidecarCaptionTracks,
  type SidecarCaptionTrack,
} from './shakaCaptionTracks';
import { ensureShakaCustomSeekControlsRegistered } from './shakaCustomSeekControls';
import {
  ensureDefaultShakaIconsRegistered,
  guardShakaControlButtonIcon,
  replaceShakaControlButtonIcon,
} from './shakaIconRegistry';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';

/**
 * Premium control layout:
 * - Bottom bar: transport, timeline, PIP, fullscreen.
 * - Top cluster (`.shaka-top-controls-cluster` + CustomPlayer CSS): quality, language, captions,
 *   rate, mute, volume — grouped in the bottom button panel for menus/events, pinned top-right.
 *
 * Custom `rewind10` / `forward10` controls perform fixed ±10 second seeks.
 */

/**
 * Bottom transport row (under the seek bar via CustomPlayer CSS `column-reverse`).
 * Quality / language / captions / rate / mute / volume are hoisted to `.shaka-top-controls-cluster`.
 */
const DEFAULT_CONTROL_PANEL_ELEMENTS: string[] = [
  'rewind10',
  'play_pause',
  'forward10',
  'mute',
  'time_and_duration',
  'spacer',
  'settings',
  'language_captions',
  'playback_rate',
  'volume',
  'picture_in_picture',
  'fullscreen',
];

const LIVE_CONTROL_PANEL_ELEMENTS: string[] = [
  'rewind10',
  'play_pause',
  'forward10',
  'mute',
  'time_and_duration',
  'spacer',
  'settings',
  'language_captions',
  'playback_rate',
  'volume',
  'picture_in_picture',
  'fullscreen',
];

/** Cast / AirPlay only — the rest live on the top button row. */
const DEFAULT_OVERFLOW_MENU_BUTTONS: string[] = ['cast', 'airplay'];

const TOP_CONTROLS_CLUSTER_CLASS = 'shaka-top-controls-cluster';
const TOP_VOLUME_STACK_CLASS = 'shaka-top-volume-stack';

/** Icon row (left → right). */
const TOP_CONTROLS_ICON_SELECTORS: readonly string[] = [
  '.shaka-resolution-button',
  '.shaka-language-captions-button',
  '.shaka-playbackrate-button',
];

/** Vertical stack: speaker icon, then volume rail centered beneath it. */
const TOP_VOLUME_STACK_SELECTORS: readonly string[] = ['.shaka-mute-button', '.shaka-volume-bar-container'];

/** Playback-rate menu only — rewind / fast-forward buttons use a single time jump (see below). */
const DEFAULT_PLAYBACK_RATES: number[] = [0.5, 0.75, 1, 1.25, 1.5];

/**
 * Shaka paints seek/volume rails with inline `linear-gradient` backgrounds (not range `accent-color`).
 * `played` / `level` must be set here; SCSS alone cannot override those fills.
 */
const DEFAULT_SEEK_BAR_COLORS = {
  base: 'rgba(255, 255, 255, 0.18)',
  buffered: 'rgba(255, 255, 255, 0.35)',
  played: 'var(--primary-btn-color)',
  adBreaks: 'rgb(255, 204, 0)',
};

const DEFAULT_VOLUME_BAR_COLORS = {
  base: 'rgba(255, 255, 255, 0.28)',
  level: 'var(--primary-btn-color)',
};

const CINEMA_MENU_CLOSE_BOUND_ATTR = 'data-cinema-close-bound';
const CINEMA_MENU_OBSERVER_BOUND_ATTR = 'data-cinema-close-observer-bound';
const VERTICAL_VOLUME_BOUND_ATTR = 'data-vertical-volume-bound';

export function formatShakaLoadError(e: unknown): string {
  if (typeof e === 'object' && e !== null) {
    const err = e as { message?: string; code?: unknown };
    if (typeof err.code === 'number') {
      return err.message || `Playback failed (Shaka ${err.code}).`;
    }
  }
  if (e instanceof Error) return e.message;
  return 'Failed to load media';
}

export function installShakaPolyfills(): void {
  shaka.polyfill.installAll();
}

export function isShakaBrowserSupported(): boolean {
  return shaka.Player.isBrowserSupported();
}

export type ShakaPlayerInstance = shaka.Player;
export type ShakaUIOverlayInstance = shaka.ui.Overlay;

/** Prefer this over `new shaka.Player(video)` — the mediaElement ctor is deprecated (removed in Shaka v5). */
export function createShakaPlayer(): shaka.Player {
  return new shaka.Player();
}

export async function attachShakaToVideo(player: shaka.Player, video: HTMLVideoElement): Promise<void> {
  await player.attach(video);
}

export function configureShakaStreaming(player: shaka.Player, isLiveStream: boolean): void {
  player.configure('streaming.rebufferingGoal', 2);
  /*
   * Text/VTT segments that return 4xx/5xx would otherwise surface as Shaka 1001 (BAD_HTTP) and can
   * interrupt or confuse startup; captions still work when sidecar `<track>` is loaded by the browser.
   */
  player.configure('streaming.ignoreTextStreamFailures', true);
  if (isLiveStream) {
    player.configure('streaming.lowLatencyMode', true);
    /*
     * liveSync continuously seeks toward the live edge — it breaks pause, catch-up/DVR scrub, and native controls UX.
     * Re-enable only for strict “always live edge” linear streams (no time-shift).
     */
    player.configure('streaming.liveSync.enabled', false);
  }
}

export function applyDrmConfiguration(player: shaka.Player, drmConfig: shaka.extern.DrmConfiguration | null): void {
  if (drmConfig) {
    player.configure({ drm: drmConfig });
  }
}

export async function loadShakaManifest(player: shaka.Player, manifestUrl: string, startSeconds?: number): Promise<void> {
  const start = startSeconds === undefined || startSeconds <= 0 ? undefined : startSeconds;
  await player.load(manifestUrl, start);
}

export function destroyShakaPlayer(player: shaka.Player): Promise<void> {
  return player.destroy();
}

/**
 * Groups top-right controls in a flex row inside the bottom button panel (stays under
 * `.shaka-bottom-controls` so menus + `shaka-no-propagation` keep working). CSS pins the cluster
 * to the top-right to align with the Cinema title row.
 */
export function markShakaTopControlsLayout(videoContainer: HTMLElement): void {
  const buttonPanel = videoContainer.querySelector('.shaka-bottom-controls .shaka-controls-button-panel');
  if (!buttonPanel) return;

  const legacyTop = videoContainer.querySelector('.shaka-top-controls');
  if (legacyTop) {
    while (legacyTop.firstChild) {
      buttonPanel.appendChild(legacyTop.firstChild);
    }
    legacyTop.remove();
  }

  let cluster = buttonPanel.querySelector(`.${TOP_CONTROLS_CLUSTER_CLASS}`) as HTMLElement | null;
  if (!cluster) {
    cluster = document.createElement('div');
    cluster.className = TOP_CONTROLS_CLUSTER_CLASS;
    buttonPanel.appendChild(cluster);
  }

  let volumeStack = cluster.querySelector(`.${TOP_VOLUME_STACK_CLASS}`) as HTMLElement | null;
  if (!volumeStack) {
    volumeStack = document.createElement('div');
    volumeStack.className = TOP_VOLUME_STACK_CLASS;
    cluster.appendChild(volumeStack);
  }

  for (const selector of TOP_CONTROLS_ICON_SELECTORS) {
    const el = videoContainer.querySelector(`.shaka-bottom-controls ${selector}`);
    if (el instanceof HTMLElement && el.parentElement !== cluster) {
      volumeStack.before(el);
    }
  }

  for (const selector of TOP_VOLUME_STACK_SELECTORS) {
    const el = videoContainer.querySelector(`.shaka-bottom-controls ${selector}`);
    if (el instanceof HTMLElement && el.parentElement !== volumeStack) {
      volumeStack.appendChild(el);
    }
  }

  if (volumeStack.parentElement !== cluster) {
    cluster.appendChild(volumeStack);
  }

  videoContainer.dataset.shakaTopControls = '1';
}

function closeShakaSettingsMenu(controls: shaka.ui.Controls, videoContainer: HTMLElement): void {
  controls.hideSettingsMenus();
  videoContainer.focus({ preventScroll: true });
}

function bindCinemaMenuOptionClose(
  menu: HTMLElement,
  controls: shaka.ui.Controls,
  videoContainer: HTMLElement,
): void {
  const buttons = menu.querySelectorAll(
    'button:not(.shaka-back-to-overflow-button):not(.shaka-cinema-menu-skip-back)',
  );
  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement) || button.hasAttribute(CINEMA_MENU_CLOSE_BOUND_ATTR)) return;
    button.setAttribute(CINEMA_MENU_CLOSE_BOUND_ATTR, '1');
    button.addEventListener('click', () => {
      window.setTimeout(() => closeShakaSettingsMenu(controls, videoContainer), 0);
    });
  });

  if (menu.hasAttribute(CINEMA_MENU_OBSERVER_BOUND_ATTR)) return;
  menu.setAttribute(CINEMA_MENU_OBSERVER_BOUND_ATTR, '1');
  const observer = new MutationObserver(() => bindCinemaMenuOptionClose(menu, controls, videoContainer));
  observer.observe(menu, { childList: true, subtree: true });
}

function bindCinemaMenuBackClose(controls: shaka.ui.Controls, videoContainer: HTMLElement): void {
  if (videoContainer.dataset.cinemaBackCloseBound === '1') return;
  videoContainer.dataset.cinemaBackCloseBound = '1';

  videoContainer.addEventListener('keydown', (event) => {
    const isBackKey = ['Escape', 'Backspace', 'BrowserBack', 'GoBack', 'Back'].includes(event.key);
    if (!isBackKey || !controls.anySettingsMenusAreOpen()) return;

    event.preventDefault();
    event.stopPropagation();
    closeShakaSettingsMenu(controls, videoContainer);
  }, true);
}

function decorateCinemaSettingsMenus(videoContainer: HTMLElement, controls: shaka.ui.Controls): void {
  const menus = videoContainer.querySelectorAll('.shaka-settings-menu');
  menus.forEach((menu) => {
    if (!(menu instanceof HTMLElement)) return;
    menu.classList.add('shaka-cinema-settings-menu');
    if (menu.classList.contains('shaka-resolutions')) {
      menu.dataset.cinemaMenuTitle = 'Quality';
    } else if (menu.classList.contains('shaka-playback-rates')) {
      menu.dataset.cinemaMenuTitle = 'Playback Speed';
    }
    bindCinemaMenuOptionClose(menu, controls, videoContainer);
  });
  bindCinemaMenuBackClose(controls, videoContainer);
}

function bindShakaControlChrome(
  videoContainer: HTMLElement,
  controls: shaka.ui.Controls,
  video: HTMLMediaElement,
): void {
  const applyStatefulIcons = () => {
    const muteIconName = video.muted || video.volume === 0 ? 'volume_cut' : 'mute';
    const fullscreenIconName = isFullscreenActive() ? 'fullscreen_exit' : 'fullscreen';
    replaceShakaControlButtonIcon(videoContainer, '.shaka-mute-button', muteIconName);
    replaceShakaControlButtonIcon(videoContainer, '.shaka-fullscreen-button', fullscreenIconName);
  };

  const iconControls: Array<{ selector: string; iconName: string }> = [
    { selector: '.shaka-resolution-button', iconName: 'settings' },
    { selector: '.shaka-language-captions-button', iconName: 'captions' },
    { selector: '.shaka-playbackrate-button', iconName: 'playback_rate' },
    { selector: '.shaka-pip-button', iconName: 'picture_in_picture' },
  ];

  const apply = () => {
    markShakaTopControlsLayout(videoContainer);
    for (const { selector, iconName } of iconControls) {
      replaceShakaControlButtonIcon(videoContainer, selector, iconName);
      guardShakaControlButtonIcon(videoContainer, selector, iconName);
    }
    decorateCinemaSettingsMenus(videoContainer, controls);
    applyStatefulIcons();
  };

  controls.addEventListener('uiupdated', apply);
  /*
   * Mute/fullscreen buttons update via `button.textContent = 'volume_off'|'fullscreen_exit'`,
   * which wipes custom icon children and does NOT dispatch `uiupdated`.
   */
  video.addEventListener('volumechange', applyStatefulIcons);
  document.addEventListener('fullscreenchange', applyStatefulIcons);
  document.addEventListener('webkitfullscreenchange', applyStatefulIcons);
  apply();
}

export type ShakaUIOverlayOptions = {
  isLiveStream: boolean;
  /** When set, overrides `isLiveStream` using `getMediaStatusFromEventState` for VCH events. */
  playlistItem?: PlaylistItem;
  /** Optional caller overrides merged on top of our minimal defaults. */
  overrides?: Partial<shaka.extern.UIConfiguration>;
};

function resolveIsLiveStreamForUi(options: ShakaUIOverlayOptions): boolean {
  if (options.playlistItem) {
    return isLiveStreamPlayback(options.playlistItem);
  }
  return options.isLiveStream;
}

/**
 * Construct a Shaka UI overlay bound to the given video container + element.
 *
 * The video element MUST already be inside `videoContainer`; Shaka renders the control bar as
 * a sibling of the `<video>` inside that container.
 */
export function createShakaUIOverlay(
  player: shaka.Player,
  videoContainer: HTMLElement,
  video: HTMLMediaElement,
  options: ShakaUIOverlayOptions,
): shaka.ui.Overlay {
  ensureDefaultShakaIconsRegistered();
  ensureShakaCustomSeekControlsRegistered();
  ensureShakaCombinedLanguageCaptionsRegistered();
  const overlay = new shaka.ui.Overlay(player, videoContainer, video);

  /*
   * For live without DVR window: remove the seek bar entirely (it would be useless) but keep
   * play/mute/fullscreen. For VOD: full control set with FF/RW, playback rates, and the seek bar
   * (which auto-renders thumbnail previews when an image track is present).
   */

  const isLiveStream = resolveIsLiveStreamForUi(options);
  const baseConfig: Partial<shaka.extern.UIConfiguration> = {
    controlPanelElements: isLiveStream ? LIVE_CONTROL_PANEL_ELEMENTS : DEFAULT_CONTROL_PANEL_ELEMENTS,
    overflowMenuButtons: DEFAULT_OVERFLOW_MENU_BUTTONS,
    playbackRates: DEFAULT_PLAYBACK_RATES,
    addBigPlayButton: true,
    addSeekBar: true,
    enableTooltips: true,
    enableKeyboardPlaybackControls: true,
    keyboardSeekDistance: DEFAULT_KEYBOARD_SMALL_SEEK_SEC,
    keyboardLargeSeekDistance: DEFAULT_KEYBOARD_LARGE_SEEK_SEC,
    doubleClickForFullscreen: true,
    singleClickForPlayAndPause: true,
    seekOnTaps: true,
    /** Fullscreen the shell (video + Shaka chrome), not the bare `<video>` tag. */
    fullScreenElement: videoContainer,
    /** Slightly longer than default 3s — premium players hold controls visible a touch longer. */
    fadeDelay: 1,
    /** Keep ad markers and rich features off until needed; reduces visual noise. */
    customContextMenu: false,
    seekBarColors: DEFAULT_SEEK_BAR_COLORS,
    volumeBarColors: DEFAULT_VOLUME_BAR_COLORS,
  };

  const mergedConfig = options.overrides ? { ...baseConfig, ...options.overrides } : baseConfig;
  overlay.configure(mergedConfig);
  markShakaTopControlsLayout(videoContainer);

  const controls = overlay.getControls();
  if (controls && video instanceof HTMLVideoElement) {
    bindShakaControlChrome(videoContainer, controls, video);
  }

  return overlay;
}

export async function destroyShakaUIOverlay(overlay: shaka.ui.Overlay | null): Promise<void> {
  if (!overlay) return;
  await overlay.destroy().catch(() => undefined);
}

/**
 * Side-load a sidecar thumbnail VTT (JWP `tracks[].kind === 'thumbnails'`) into Shaka so the
 * UI seek bar auto-renders previews. No-op (and safe) for live streams and missing URLs.
 *
 * Shaka throws `CANNOT_ADD_EXTERNAL_THUMBNAILS_TO_LIVE_STREAM (4045)` for live; we gate before calling.
 */
export async function addExternalThumbnailsTrack(
  player: shaka.Player,
  vttUrl: string | null | undefined,
  isLiveStream: boolean,
): Promise<void> {
  if (!vttUrl || isLiveStream) return;
  try {
    /*
     * If the manifest already declared image tracks, Shaka's seek bar uses them automatically and we
     * skip the sidecar — having two thumbnail sources would race.
     */
    const existing = player.getImageTracks();
    if (existing && existing.length > 0) return;
    await player.addThumbnailsTrack(vttUrl);
  } catch {
    /* Sidecar VTT failure is non-fatal; we simply have no thumbnail previews. */
  }
}

/**
 * Side-load JWP-style caption sidecars into Shaka after `load()`.
 * MSE playback only renders text through Shaka's text pipeline — native `<track>` mode alone is not enough.
 */
export async function addExternalCaptionTracksFromVideo(
  player: shaka.Player,
  video: HTMLVideoElement,
  isLiveStream: boolean,
  configuredTracks?: readonly SidecarCaptionTrack[],
): Promise<void> {
  const tracks = mergeSidecarCaptionTracks(configuredTracks, collectSidecarCaptionsFromVideo(video));
  await addSidecarCaptionTracks(player, tracks, isLiveStream, video);
}

export { isPlaceholderCaptionTrackSrc, type SidecarCaptionTrack } from './shakaCaptionTracks';
